import Link from "next/link";
import { getServiceCategories } from "@/content/services";
import type { MetaTagsProps } from "@/types";
import {
  GoogleServiceArea,
  ServiceCategoryNavigationBlock,
  type ServiceCategoryNavigationBlockGroup,
} from "@modes0/ui-components";
import { BUSINESS_NAME, OG_PATH, WEBSITE_URL, googleDetails } from "@/content";
import {PageSeo} from '@modes0/ui-components-seo';
import {seoSiteProfile} from '@/content/seo';

const brandAccentColor = "#28aae1";
const brandAccentDarkColor = "#15171e";

const servicesMetaTags: MetaTagsProps = {
  title: `Security Services Sydney | ${BUSINESS_NAME}`,
  description: `Explore ${BUSINESS_NAME} security services for Sydney properties, from CCTV, alarms, intercoms and access control to guards, patrols, fire alarms and security cabling.`,
  keywords:
    "security services Sydney, CCTV installation Sydney, alarm systems Sydney, access control Sydney, security guards Sydney, mobile patrols Sydney, intercoms Sydney",
  url: `${WEBSITE_URL}/services/`,
  ogImage: `${OG_PATH}services.jpg`,
  type: "website",
};

const getCategoryHref = (
  category: ReturnType<typeof getServiceCategories>[number],
) => `/${category.categoryType}/${category.slug}`;

const getServiceHref = (
  service: NonNullable<
    ReturnType<typeof getServiceCategories>[number]["subCategories"]
  >[number],
  category: ReturnType<typeof getServiceCategories>[number],
) => service.link || `${getCategoryHref(category)}/${service.slug}`;

const getServicesNavigationGroups = (
  serviceCategories: ReturnType<typeof getServiceCategories>,
): ServiceCategoryNavigationBlockGroup[] =>
  serviceCategories.map((category) => ({
    id: category.id,
    title: category.name,
    description: category.description?.[0] || category.long_name,
    href: getCategoryHref(category),
    ariaLabel: `View ${category.name} services`,
    accentColor: brandAccentColor,
    items: (category.subCategories || []).map((service) => ({
      id: service.id,
      title: service.title,
      description: service.description,
      href: getServiceHref(service, category),
      accentColor: brandAccentColor,
      accentDarkColor: brandAccentDarkColor,
      ctaLabel: `View ${service.title}`,
    })),
  }));

export default function Services() {
  const serviceCategories = getServiceCategories();
  const serviceNavigationGroups =
    getServicesNavigationGroups(serviceCategories);

  return (
    <>
      <PageSeo meta={servicesMetaTags} siteProfile={seoSiteProfile} includeBusinessSchema />
      <h1 className="sr-only">Security services from {BUSINESS_NAME}</h1>
      <ServiceCategoryNavigationBlock
        groups={serviceNavigationGroups}
        Link={Link}
        overline={BUSINESS_NAME}
        title="Security services"
        description={`${BUSINESS_NAME} helps Sydney homes, businesses and worksites with electronic security, guarding, patrols, consultation, fire alarm support and security cabling.`}
        navTitle="Service categories"
        itemCtaLabel="View service"
        showItemDescriptions
        accentColor={brandAccentColor}
        backgroundColor="#edf7fc"
        panelBackgroundColor="#ffffff"
        columns="auto"
        ariaLabel="Security services and categories"
      />
      <div className="py-10">
        <GoogleServiceArea googleDetails={googleDetails} />
      </div>
    </>
  );
}
